// SimpleSignIn.js
// Clean, minimal sign-in page for existing users who already have accounts

import React from 'react';
import { X } from 'lucide-react';
import logo from '../src/images/70Lives Beauty Ledger Logo.png';

const SimpleSignIn = ({
  isOpen,
  onClose,
  onGoogleSignIn,
  rememberMe = true,
  onRememberChange = () => {},
  variant = 'modal',
  onSwitchToSignUp
}) => {
  if (!isOpen) return null;

  const handleGoogleSignIn = async () => {
    try {
      await onGoogleSignIn();
      if (onClose) onClose();
    } catch (error) {
      console.error('Sign-in error:', error);
    }
  };

  const isPage = variant === 'page';
  const wrapperClass = isPage
    ? 'fixed inset-0 bg-accent-gradient flex items-center justify-center p-6 z-40'
    : 'fixed inset-0 bg-black bg-opacity-100 flex items-center justify-center z-50 p-4';
  const cardClass = isPage
    ? 'bg-white rounded-2xl w-full max-w-md mx-auto shadow-2xl'
    : 'bg-white rounded-2xl w-full max-w-md mx-auto relative';
  const contentClass = isPage ? 'p-8' : 'p-8 pt-16';

  return (
    <div className={wrapperClass}>
      <div className={cardClass}>
        {!isPage && (
          <button
            onClick={onClose}
            aria-label="Close sign in"
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition z-10"
          >
            <X className="w-6 h-6" />
          </button>
        )}

        <div className={contentClass}>
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <img src={logo} alt="Business Finance Tracker" className="h-12 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h2>
            <p className="text-gray-600 text-sm">
              Sign in to your business finance tracker
            </p>
          </div>

          {/* Sign In Button */}
          <button
            onClick={handleGoogleSignIn}
            className="w-full bg-[#4FB368] text-white px-6 py-3 rounded-lg hover:bg-[#45a05d] transition flex items-center justify-center gap-3 mb-6 font-medium"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M12.545,12.151L12.545,12.151c0,1.054,0.855,1.909,1.909,1.909h3.536c-0.607,1.972-2.405,3.404-4.536,3.404c-2.626,0-4.754-2.128-4.754-4.754s2.128-4.754,4.754-4.754c1.26,0,2.405,0.492,3.255,1.29l1.534-1.534C16.455,6.892,14.426,6,12.545,6C8.901,6,6,8.901,6,12.545s2.901,6.545,6.545,6.545c4.017,0,6.545-2.927,6.545-6.545V11.5h-6.545V12.151z"
              />
            </svg>
            Continue with Google
          </button>
          <label className="flex items-center justify-center gap-2 text-sm text-gray-700 mb-4">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => onRememberChange(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 accent-[#4FB368]"
            />
            Remember me on this device
          </label>

          {onSwitchToSignUp && (
            <div className="text-center mb-4">
              <button
                onClick={onSwitchToSignUp}
                className="text-sm text-gray-600 hover:text-gray-800 font-medium"
              >
                New here? Choose a plan
              </button>
            </div>
          )}

          {/* Security Info */}
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <p className="text-xs text-gray-600 leading-relaxed">
              Your financial data is stored securely in your personal Google Drive. We never store your data on our servers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleSignIn;
